/**
 * Bedsheet Bundle: "pick any 3 bedsheets of the sheet set, get CFG.pct% off all 3" (theme setting). Each of the 3 sheets has
 * its own print AND its own size (Single/Double = 90 x 60, Queen/King = 90 x 100), so a Bundle can be
 * mixed (e.g. 2 Single/Double + 1 Queen/King). Built from the "SleepyCat Bedsheet Bundle" design canvas.
 *
 * Two surfaces share this controller:
 *  - every PDP: the FIRST card in the "Great to pair with" rail (pdp-cross-sell.js asks for its HTML
 *    via BedsheetBundle.cardHtml and passes every cart snapshot to BedsheetBundle.syncCart);
 *  - the cart (drawer, /cart): "Add 1 more bedsheet" (2 loose sheets → pick a 3rd), opened by
 *    cart-bedsheet-bundle.js, which loads this file on demand on pages without the rail.
 * Both open "Choose your 3 bedsheets": a mode of the rail's .pdp-xsell-modal shell (bottom sheet on
 * phones, a 2-column modal on desktop). The rail's variant modal is untouched.
 *
 * The sheet: a rail of the 3 sheets (one is "Editing"), the size for the editing sheet, the 8 prints.
 * Tapping a print sets the editing sheet's print; the same print can go on more than one sheet. The print
 * tiles and the size control show the EDITING sheet: its colour selected when it has one, none when it's
 * empty; the tray shows all 3. While the Bundle is filled the first time, picking a print moves to the next
 * empty sheet. An empty sheet's size: the bed size on this page → a bed-sized product in the cart → the
 * shopper's last pick → the first size (Single/Double); changing a size also moves the empty sheets to it.
 *
 * Pricing is Shopify's: App A ("SleepyCat Bedsheet Bundle") takes `pct`% off the selling price of the
 * lines that share one `_sheet_bundle_id` (this file writes it), for at most `maxBundles` bundles per
 * cart — at that cap the card shows the Bundle in the cart with "Edit" and the sheet won't add another.
 * This file only QUOTES the price: round((100 − pct)% × the 3 sheets' prices) in whole rupees, the same
 * rule as the cart's bundle line (at 20%: 3 × ₹1,029 = ₹3,087 → ₹2,470; 2 × ₹1,029 + ₹1,289 = ₹3,347 → ₹2,678).
 *
 * Cart lines: 3 lines, one per sheet, each with `_sheet_bundle` (title), `_sheet_bundle_id` and
 * `_sheet_bundle_slot`; the drawer groups them by `_sheet_bundle_id` (snippets/bedsheet-bundle-cart-line.liquid).
 */
(function () {
  'use strict';

  if (window.BedsheetBundle) return;

  var DATA_ID = 'bedsheet-bundle-data';
  var PICKS_KEY = 'bbPicks:v2';      // an unfinished Bundle, for the card's "2 of 3 chosen · Continue" (this tab)
  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  var CFG = null;          // config + normalised variants
  var configPromise = null;
  var colours = [];        // [{ name, cat, img }] in the product's own order
  var S = freshState();    // sheet state
  var modal = null, els = {};
  var closeTimer = null, returnFocusTo = null, pulseTimer = null;
  var cartBundles = 0;     // Bedsheet Bundles in the cart (distinct _sheet_bundle_id)
  var cartBundle = null;   // the newest one: { id, lines: [{ id, qty, slot }] } (the card's Edit)
  var cartSig = '';
  var viewTracked = false;

  function freshState() {
    return {
      open: false, mode: 'new', source: 'rail',
      slots: [null, null, null],   // print (colour name) per sheet
      sz: [null, null, null],      // size value per sheet ("90 x 60"), null = not chosen
      active: 0,                   // the sheet being edited
      filled: false,               // all 3 prints chosen once: auto-advance is off from then on
      ref: null,                   // where the default size came from: { size, text, source }
      baseKeys: [],                // complete mode: the loose cart sets used, one entry per sheet
      baseSig: '',                 // complete mode: those sets as the nudge listed them at the tap
      editId: null, editQty: 1, orig: '',
      hint: '', note: '', pulseRail: false, pulseSize: false, adding: false
    };
  }

  // ---- Config / data -----------------------------------------------------------------------
  function readInline() {
    var el = document.getElementById(DATA_ID);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function setConfig(cfg) {
    CFG = cfg;
    var seen = {};
    colours = [];
    (cfg.variants || []).forEach(function (v) {
      if (seen[v.color]) return;
      seen[v.color] = true;
      colours.push({ name: v.color, cat: v.cat, img: v.img });
    });
    return CFG;
  }
  // From /products/<handle>.js (pages without the inline PDP data: the drawer offer).
  function variantsFromProductJs(p) {
    var idx = { cat: 0, color: 1, size: 2 };
    (p.options || []).forEach(function (o, i) {
      var n = String(o.name || o).toLowerCase();
      if (n === 'category') idx.cat = i;
      else if (n === 'color' || n === 'colour') idx.color = i;
      else if (n === 'dimension' || n === 'size') idx.size = i;
    });
    return (p.variants || []).map(function (v) {
      var fi = v.featured_image && (v.featured_image.src || v.featured_image);
      var img = fi || (p.featured_image || '');
      if (img && img.indexOf('//') === 0) img = 'https:' + img;
      return {
        id: v.id, cat: v.options[idx.cat], color: v.options[idx.color], size: v.options[idx.size],
        price: v.price, compare: v.compare_at_price || 0, available: v.available,
        img: img ? img + (img.indexOf('?') === -1 ? '?' : '&') + 'width=240' : ''
      };
    });
  }
  function ensureConfig(baseCfg) {
    if (CFG && CFG.variants) return Promise.resolve(CFG);
    var inline = readInline();
    if (inline && inline.variants) return Promise.resolve(setConfig(inline));
    if (configPromise) return configPromise;
    var cfg = baseCfg || inline;
    if (!cfg || !cfg.handle) return Promise.reject(new Error('Bedsheet Bundle: no config'));
    configPromise = fetch('/products/' + cfg.handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
      .then(function (p) { return setConfig(Object.assign({}, cfg, { variants: variantsFromProductJs(p) })); })
      .catch(function (e) { configPromise = null; throw e; });
    return configPromise;
  }
  // Inline data is there from the start on the PDP.
  (function () { var inline = readInline(); if (inline && inline.variants) setConfig(inline); })();

  function variantFor(colour, size) {
    var list = CFG.variants;
    for (var i = 0; i < list.length; i++) if (list[i].color === colour && list[i].size === size) return list[i];
    return null;
  }
  function isSoldOut(colour, size) { var v = variantFor(colour, size); return !v || !v.available; }
  function allSold(colour) { return CFG.sizes.every(function (z) { return isSoldOut(colour, z.value); }); }
  function colour(name) {
    for (var i = 0; i < colours.length; i++) if (colours[i].name === name) return colours[i];
    return { name: name, cat: '', img: '' };
  }
  function sizeInfo(value) {
    var z = (CFG.sizes || []).filter(function (x) { return x.value === value; })[0];
    return z || { value: value, label: value, dims: value, say: value };
  }
  function lowSize() { return CFG.sizes[0].value; }
  // The hero photo at a width (Shopify's CDN resizes theme assets; asset_url already carries ?v=).
  function heroAt(w) { var u = CFG.hero || ''; return u ? u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=' + w : ''; }
  // The sheet product's page, under the store's current locale/market root.
  function productUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(CFG.handle);
  }
  function pickedCount(slots) { return (slots || S.slots).filter(Boolean).length; }

  // ---- Money (paise in, whole rupees out) ------------------------------------------------------
  function rupees(paise) { return '₹' + Math.round((Number(paise) || 0) / 100).toLocaleString('en-IN'); }
  function unitPrice(size) {
    var unit = 0;
    CFG.variants.some(function (v) { if (v.size === size && v.available) { unit = v.price; return true; } return false; });
    if (!unit) CFG.variants.some(function (v) { if (v.size === size) { unit = v.price; return true; } return false; });
    return unit;
  }
  // A sheet without a size counts at the lower size's price (the "From" price).
  function quote(slots, sizes) {
    var full = 0;
    for (var i = 0; i < CFG.pick; i++) {
      var z = (sizes && sizes[i]) || lowSize();
      var v = slots && slots[i] ? variantFor(slots[i], z) : null;
      full += v ? v.price : unitPrice(z);
    }
    var bundle = Math.round(full * (100 - CFG.pct) / 100 / 100) * 100; // whole rupees
    return { full: full, bundle: bundle, save: full - bundle };
  }
  // "All 3 for Single/Double beds" or "2 Single/Double · 1 Queen/King".
  function composition(sizes) {
    var counts = {};
    sizes.forEach(function (z) { if (z) counts[z] = (counts[z] || 0) + 1; });
    var keys = Object.keys(counts);
    if (keys.length === 1) return 'All ' + counts[keys[0]] + ' for ' + sizeInfo(keys[0]).label + ' beds';
    return CFG.sizes.filter(function (z) { return counts[z.value]; })
      .map(function (z) { return counts[z.value] + ' ' + z.label; }).join(' · ');
  }

  // ---- Where the default sheet size comes from -------------------------------------------------
  // Only a mattress page sets it ("Fits your Queen mattress"); config sizeRef is 'mattress' there and
  // '' everywhere else (toppers, the mattress cover, baby and tri-fold mattresses, beds, bedding…), where
  // the sheets start at the first size (Single/Double) with no fit text.
  // A bed size in a list of option values: Single/Double/Queen/King by name, or (mattresses) by the
  // width in "L x W x H".
  function bedSizeIn(values, byWidth) {
    for (var i = 0; i < values.length; i++) {
      if (values[i] && CFG.sizeFor[values[i]]) return { name: values[i], size: CFG.sizeFor[values[i]] };
    }
    if (!byWidth) return null;
    for (var j = 0; j < values.length; j++) {
      var m = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i.exec(values[j] || '');
      if (!m) continue;
      var w = parseFloat(m[2]);
      var name = w <= 38 ? 'Single' : w <= 54 ? 'Double' : w <= 67 ? 'Queen' : 'King';
      return { name: name, size: CFG.sizeFor[name] };
    }
    return null;
  }
  function productVariants() {
    if (window.productData && window.productData.variants) return window.productData.variants;
    var el = document.getElementById('ProductJson');
    try { return el ? JSON.parse(el.textContent).variants || [] : []; } catch (e) { return []; }
  }
  function currentVariantId() {
    var pi = document.querySelector('product-info');
    var input = (pi && pi.querySelector('input[name="id"]')) ||
      document.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]');
    return input ? parseInt(input.value, 10) : null;
  }
  // The option values chosen on this page right now. A bed size can be chosen before the variant changes:
  // on a mattress, tapping Queen keeps the old variant (and its dimension checked) until a Queen dimension
  // is picked, so the variant alone would still say the old size.
  function pageChoices() {
    var vs = document.querySelector('product-info variant-selects');
    return vs ? [].map.call(vs.querySelectorAll('input[type="radio"]:checked, select'), function (el) { return el.value; }) : [];
  }
  // 1. The bed size selected on this product page (mattress, protector, bed, fitted sheet…): the size
  // chosen on the page by name, else the selected variant's (by name, or a mattress's width).
  function pageRef(variantId) {
    if (!CFG.sizeRef) return null;
    var b = bedSizeIn(pageChoices(), false);
    if (!b) {
      var id = String(variantId || currentVariantId() || '');
      var v = productVariants().filter(function (x) { return String(x.id) === id; })[0];
      if (v) b = bedSizeIn([v.option1, v.option2, v.option3], CFG.sizeRef === 'mattress');
    }
    return b && b.size ? { size: b.size, text: 'Fits your ' + b.name + ' ' + (CFG.sizeNoun || 'mattress'), source: 'page' } : null;
  }
  function sizeRef(variantId) { return pageRef(variantId); }

  // ---- An unfinished Bundle, remembered for this browser session ---------------------------------
  function loadPicks() {
    try {
      var p = JSON.parse(sessionStorage.getItem(PICKS_KEY) || 'null');
      if (p && Array.isArray(p.slots) && p.slots.length === CFG.pick && Array.isArray(p.sz)) return p;
    } catch (e) {}
    return null;
  }
  function savePicks() {
    try {
      if (S.mode !== 'new') return;
      if (!S.slots.some(Boolean)) sessionStorage.removeItem(PICKS_KEY);
      else sessionStorage.setItem(PICKS_KEY, JSON.stringify({ slots: S.slots, sz: S.sz }));
    } catch (e) {}
  }
  function clearPicks() { try { sessionStorage.removeItem(PICKS_KEY); } catch (e) {} }

  // ---- Analytics -------------------------------------------------------------------------------
  // Pushed after the tap's frame has painted (rAF → setTimeout 0, the web.dev INP yield pattern):
  // one SCAnalytics.push costs ~60 ms at 4× CPU (GTM reacts synchronously), so it must not run
  // inside the interaction.
  function track(name, params) {
    var payload = params || {};
    requestAnimationFrame(function () {
      setTimeout(function () {
        try {
          if (window.SCAnalytics && typeof window.SCAnalytics.push === 'function') window.SCAnalytics.push(name, payload);
          else (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload));
        } catch (e) {}
      }, 0);
    });
  }
  // Run right after the next frame is painted (rAF → setTimeout 0): work that forces style/layout (focus,
  // measuring) goes here, so it's never part of a tap's response (INP).
  function afterPaint(fn) { requestAnimationFrame(function () { setTimeout(fn, 0); }); }
  // Run when the browser is idle (or after 2 s).
  function idle(fn) {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 1200);
  }

  // ---- Helpers ---------------------------------------------------------------------------------
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var ICON_CHECK = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  var ICON_INFO = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16.5v.5"/></svg>';
  var ICON_X = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  // ==== Rail card ================================================================================
  var cardVariant = null;
  function maxBundles() { return Math.max(1, Number(CFG && CFG.maxBundles) || 1); }
  function cardHtml(variantId) {
    if (!CFG || !CFG.enabled || !CFG.variants) return '';
    var buyable = colours.filter(function (c) { return !allSold(c.name); });
    if (buyable.length < CFG.pick) return '';
    cardVariant = variantId || null;
    if (!viewTracked) {
      viewTracked = true;
      var ref = sizeRef(cardVariant);
      track('sheet_bundle_card_view', { slot: CFG.slot, size_source: ref ? ref.source : 'none', state: cardState() });
      idle(prepare); // build the sheet + its tile photos before the first tap
    }
    return renderCard();
  }

  // The sheet's DOM and the 8 print tiles are built once, in idle time after the card shows, so a tap only
  // updates the sheets, the size and the footer, then slides the sheet in.
  function prepare() {
    if (!CFG || !CFG.variants || modal) return;
    buildModal();
    renderTiles();
    watchRail();
    readVp();
  }
  // The tiles' photos (8 × ~10 KB at 240 px) are fetched only once the shopper is near the offer: the rail
  // within ~300 px of the screen, or the sheet opening. Not on every product page view, and never in the
  // page's own loading. Not lazy-loading: images inside the hidden sheet would start only when it opens.
  var photosOn = false, photoWatch = null;
  function loadPhotos() {
    if (photosOn) return;
    photosOn = true;
    if (photoWatch) { photoWatch.disconnect(); photoWatch = null; }
    if (!els.tiles) return;
    [].forEach.call(els.tiles.querySelectorAll('img[data-src]'), function (img) {
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
    });
  }
  function watchRail() {
    if (photosOn || photoWatch) return;
    var rail = document.getElementById('pdp-cross-sell-section');
    if (!rail || !('IntersectionObserver' in window)) { loadPhotos(); return; }
    photoWatch = new IntersectionObserver(function (entries) {
      if (entries.some(function (en) { return en.isIntersecting; })) loadPhotos();
    }, { rootMargin: '300px 0px' });
    photoWatch.observe(rail);
  }
  // default | progress (an unfinished Bundle) | added (in cart, below the cap) | full (cap reached: Edit)
  function cardState() {
    if (cartBundles >= maxBundles()) return 'full';
    if (cartBundles > 0) return 'added';
    var p = loadPicks();
    return p && pickedCount(p.slots) > 0 ? 'progress' : 'default';
  }
  function renderCard() {
    var st = cardState();
    var p = st === 'progress' ? loadPicks() : null;
    var mine = st === 'full' ? bundlePick(cartBundle) : null;
    var ref = sizeRef(cardVariant);
    // The sizes the sheet will open with: picked sheets keep theirs, empty ones take this page's bed size
    // (none on a page without one: the card then says "From", the sheet starts them at Single/Double).
    var sizes = mine ? mine.sz : (p ? p.slots.map(function (c, i) { return (c && p.sz[i]) || (ref ? ref.size : null); }) :
      [0, 1, 2].map(function () { return ref ? ref.size : null; }));
    var from = !mine && sizes.some(function (z) { return !z; });
    var q = quote(mine ? mine.slots : (p ? p.slots : null), sizes);
    var n = p ? pickedCount(p.slots) : 0;
    var inCart = st === 'added' || st === 'full';
    var status = st === 'progress' ? n + ' of ' + CFG.pick + ' chosen' : (st === 'full' ? 'In your cart' : 'Save ' + rupees(q.save));
    // The card never edits (each bundle line in the cart has its own "Edit colours & sizes"): with bundles
    // in the cart it still adds a NEW one, until the per-cart cap, where it opens the cart instead.
    var cta = st === 'progress' ? 'Continue' : (st === 'full' ? 'View cart' : 'Add');
    var aria = st === 'progress'
      ? 'Continue your bedsheet bundle, ' + n + ' of ' + CFG.pick + ' chosen'
      : (st === 'full' ? 'View cart, ' + maxBundles() + ' ' + CFG.title + (maxBundles() > 1 ? 's' : '') + ' is the most per order' : 'Add a ' + CFG.title + ', pick any ' + CFG.pick + ' bedsheets');
    // The Bundle's own photo (the folded 3-stack) in every state; it links to the sheet product's page.
    return '<div class="bb-card" data-state="' + st + '">' +
      '<a class="bb-card__media" href="' + esc(productUrl()) + '" aria-label="See the bedsheets in the ' + esc(CFG.title) + '">' +
        // ~160 CSS px of the photo shows (cover crop): 240/360/480 by screen density, resized + WebP/AVIF by
        // Shopify's CDN (the 640 px JPG is 42 KB; 480 is ~29, 360 ~17, 240 ~10).
        '<img src="' + esc(heroAt(360)) + '" srcset="' + esc(heroAt(240)) + ' 240w, ' + esc(heroAt(360)) + ' 360w, ' + esc(heroAt(480)) + ' 480w" sizes="160px" alt="Three bedsheet sets folded in a stack" width="640" height="480" loading="lazy">' +
        '<span class="bb-card__badge">' + CFG.pct + '% OFF</span>' +
        (inCart
          ? '<span class="bb-card__incart">' + ICON_CHECK + 'In your cart</span>'
          : '<span class="bb-card__label">Pick any ' + CFG.pick + ' bedsheets</span>') +
      '</a>' +
      '<div class="bb-card__body">' +
        '<div class="bb-card__tags">' + (CFG.tags || []).slice(0, 2).map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('') + '</div>' +
        '<div class="bb-card__title' + (st === 'progress' ? ' is-two-line' : '') + '">' + (st === 'progress' ? 'Build your bedsheet bundle' : esc(CFG.title)) + '</div>' +
        '<div class="bb-card__foot">' +
          '<span class="bb-card__status' + (st === 'progress' ? ' is-progress' : '') + '">' + esc(status) + '</span>' +
          '<div class="bb-card__row">' +
            '<div class="bb-card__prices">' + (from ? '<span class="bb-card__from">From</span>' : '') +
              '<span class="bb-card__price">' + rupees(q.bundle) + '</span><s>' + rupees(q.full) + '</s></div>' +
            '<button type="button" class="bb-card__cta' + (st === 'full' ? ' is-secondary' : '') + '" aria-haspopup="dialog" aria-label="' + esc(aria) + '">' + cta + '</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }
  function refreshCard() {
    var card = document.querySelector('#pdp-cross-sell-section .bb-card');
    if (!card || !CFG) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = renderCard();
    var next = wrap.firstChild;
    if (card.outerHTML !== next.outerHTML) card.replaceWith(next);
  }
  // A bed size tapped on the page changes the card's sizes and price even before the variant changes
  // (see pageChoices). After the tap's frame has painted, like track().
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('product-info variant-selects')) return;
    requestAnimationFrame(function () { setTimeout(refreshCard, 0); });
  });
  // Bundles in a cart: per `_sheet_bundle_id`, the smallest line quantity (= that bundle's quantity).
  function countBundles(cart) {
    var byId = {};
    ((cart && cart.items) || []).forEach(function (it) {
      var id = it.properties && it.properties._sheet_bundle_id;
      if (!id) return;
      byId[id] = byId[id] == null ? it.quantity : Math.min(byId[id], it.quantity);
    });
    return Object.keys(byId).reduce(function (sum, id) { return sum + byId[id]; }, 0);
  }
  // The newest Bundle in a cart (/cart.js lists the newest lines first): its lines by variant + slot.
  function newestBundle(cart) {
    var b = null;
    ((cart && cart.items) || []).forEach(function (it) {
      var id = it.properties && it.properties._sheet_bundle_id;
      if (!id || (b && b.id !== id)) return;
      if (!b) b = { id: id, lines: [] };
      b.lines.push({ id: it.variant_id, qty: it.quantity, slot: parseInt(it.properties._sheet_bundle_slot, 10) || b.lines.length + 1 });
    });
    return b;
  }
  // A cart Bundle as sheet picks: { slots, sz, qty } (sizes may differ), or null when it isn't a whole
  // Bundle of this product (lines removed elsewhere, or the config isn't loaded yet).
  function bundlePick(b) {
    if (!b || !CFG || !CFG.variants || b.lines.length !== CFG.pick) return null;
    var lines = b.lines.slice().sort(function (x, y) { return x.slot - y.slot; });
    var slots = [], sz = [], qty = Infinity;
    for (var i = 0; i < lines.length; i++) {
      var v = CFG.variants.filter(function (x) { return String(x.id) === String(lines[i].id); })[0];
      if (!v) return null;
      slots.push(v.color); sz.push(v.size); qty = Math.min(qty, lines[i].qty);
    }
    return { slots: slots, sz: sz, qty: qty };
  }
  function syncCart(cart) {
    var count = countBundles(cart);
    var b = newestBundle(cart);
    var sig = count + '|' + (b ? b.id + ':' + b.lines.map(function (l) { return l.id + 'x' + l.qty; }).join(',') : '');
    if (sig === cartSig) return;
    cartSig = sig; cartBundles = count; cartBundle = b;
    refreshCard();
  }

  // ==== Sheet ====================================================================================
  // Phone (and tablet): header, then one scroll area (sheets, size, prints) above a sticky footer.
  // Desktop (≥900px): an 840px modal, the sheets + footer in a left column, size + prints on the right.
  var DESK_MQ = window.matchMedia ? window.matchMedia('(min-width: 900px)') : null;
  function isDesk() { return !!(DESK_MQ && DESK_MQ.matches); }
  function buildModal() {
    if (modal) return;
    var el = document.createElement('div');
    el.id = 'bbModal';
    el.className = 'pdp-xsell-modal pdp-xsell-modal--bb';
    el.setAttribute('hidden', '');
    el.innerHTML =
      '<div class="pdp-xsell-modal__overlay" data-bb-close="scrim"></div>' +
      '<div class="pdp-xsell-modal__panel" role="dialog" aria-modal="true" aria-labelledby="bbTitle">' +
        '<div class="pdp-xsell-modal__header bb-head">' +
          '<div class="bb-head__text"><h3 class="pdp-xsell-modal__title" id="bbTitle">Choose your 3 bedsheets</h3><span class="bb-head__pill" id="bbPill"></span></div>' +
          '<button type="button" class="pdp-xsell-modal__close bb-close" data-bb-close="x" aria-label="Close">' + ICON_X + '</button>' +
        '</div>' +
        '<div class="bb-body">' +
          '<div class="bb-side" id="bbSide"></div>' +
          '<div class="bb-scroll" id="bbScroll">' +
            '<div class="bb-rail" id="bbRail"></div>' +
            '<div class="bb-sizebox" id="bbSize"></div>' +
            '<div class="bb-tiles" id="bbTiles"></div>' +
          '</div>' +
        '</div>' +
        '<div class="bb-foot" id="bbFoot"></div>' +
      '</div>';
    document.body.appendChild(el);
    modal = el;
    els = {
      panel: el.querySelector('.pdp-xsell-modal__panel'), pill: el.querySelector('#bbPill'),
      side: el.querySelector('#bbSide'), scroll: el.querySelector('#bbScroll'), rail: el.querySelector('#bbRail'),
      size: el.querySelector('#bbSize'), tiles: el.querySelector('#bbTiles'), foot: el.querySelector('#bbFoot')
    };
    el.addEventListener('click', onModalClick);
    if (DESK_MQ) {
      var onChange = function () { placeLayout(); if (S.open) renderAll(); };
      if (DESK_MQ.addEventListener) DESK_MQ.addEventListener('change', onChange);
      else if (DESK_MQ.addListener) DESK_MQ.addListener(onChange);
    }
    placeLayout();
  }
  // Move the sheets rail and the footer between the phone and desktop layouts.
  function placeLayout() {
    var desk = isDesk();
    modal.classList.toggle('is-desk', desk);
    if (desk) {
      if (els.rail.parentNode !== els.side) els.side.appendChild(els.rail);
      if (els.foot.parentNode !== els.side) els.side.appendChild(els.foot);
    } else {
      if (els.rail.parentNode !== els.scroll) els.scroll.insertBefore(els.rail, els.scroll.firstChild);
      if (els.foot.parentNode !== els.panel) els.panel.appendChild(els.foot);
    }
  }

  function open(opts) {
    opts = opts || {};
    return ensureConfig(opts.config).then(function () {
      buildModal();
      if (!els.tiles.firstChild) renderTiles();
      loadPhotos();
      S = freshState();
      S.source = opts.source || 'rail';
      S.ref = sizeRef();
      // An empty sheet always shows a size: this page's mattress size, else the first size
      // (Single/Double). Never "no size".
      var refSize = (S.ref && S.ref.size) || lowSize();
      var editB = opts.mode === 'edit' ? (opts.bundle || cartBundle) : null;
      var mine = editB ? bundlePick(editB) : null;
      if (opts.mode === 'complete') {
        // The cart's loose sheets (1 to CFG.pick, any colour, any size) in place at their own sizes; the
        // rest are to choose, at the mattress-mapped size if there is one, else the loose sheets' size.
        S.mode = 'complete';
        var base = (opts.base || []).slice(0, CFG.pick);
        var bsz = opts.sizes || [];
        var mapped = S.ref ? S.ref.size : null;
        var fill = mapped || bsz[0] || refSize;
        S.baseKeys = (opts.keys || []).slice(0, base.length);
        S.baseSig = setsSig(opts.sig != null ? opts.sig : JSON.stringify(opts.keys || []));
        S.slots = []; S.sz = [];
        for (var i = 0; i < CFG.pick; i++) { S.slots.push(base[i] || null); S.sz.push((base[i] && bsz[i]) || fill); }
        var open1 = nextOpen(S.slots, -1);
        S.filled = open1 < 0;
        S.active = open1 >= 0 ? open1 : 0;
      } else if (mine) {
        // Edit the Bundle in the cart: its prints and sizes, changed in place (Update replaces its lines).
        S.mode = 'edit';
        S.editId = editB.id;
        S.editQty = mine.qty;
        S.slots = mine.slots.slice();
        S.sz = mine.sz.slice();
        S.filled = true;
        S.active = 0;
        S.orig = S.sz.join('|') + '|' + S.slots.join('|');
      } else {
        var p = loadPicks();
        S.slots = p ? p.slots.slice() : [null, null, null];
        // Sheets already picked keep their size; empty ones take this page's mapped/default size.
        S.sz = S.slots.map(function (c, i) { return (c && p && p.sz[i]) || refSize; });
        S.filled = S.slots.every(Boolean);
        var first = nextOpen(S.slots, -1);
        S.active = first >= 0 ? first : 0;
      }
      renderAll();
      show(opts.opener);
      fitOnOpen();
      track('sheet_bundle_sheet_open', { source: S.source, mode: S.mode, size_source: S.ref ? S.ref.source : 'none', prefilled: pickedCount() });
    }).catch(function (e) { console.error('Bedsheet Bundle:', e); });
  }

  // Phone: when the sheet can't show every colour, keep at least half of the last row (Solids) above the
  // sticky footer as a peek, by tightening the sheets rail (shorter photos, then no progress row) —
  // never the colour tiles or the sheet's height.
  // Measuring forces layout (up to 3 passes, ~250 ms on a slow phone), so it never runs inside the tap: the
  // first open measures right after the sheet's first frame (while it slides in), and the result is kept
  // per viewport size, so later opens apply it before the first frame. A resize re-measures.
  // The key is the viewport size, read only where layout is already being done (idle, the measurement):
  // reading innerWidth/innerHeight right after the sheet is shown would itself force a layout.
  var fitFrame = 0, fitBySize = {}, vpKey = '';
  function readVp() { vpKey = window.innerWidth + 'x' + window.innerHeight; }
  window.addEventListener('resize', function () { vpKey = ''; }, { passive: true });
  function applyFit(level) {
    modal.classList.toggle('is-tight', level >= 1);
    modal.classList.toggle('is-tighter', level >= 2);
  }
  function fitPhone() {
    if (!modal || !S.open) return;
    applyFit(0);
    if (isDesk()) return;
    var short = function () {
      var rows = els.tiles.querySelectorAll('.bb-group');
      var photo = rows.length ? rows[rows.length - 1].querySelector('.bb-tile__photo') : null;
      if (!photo) return 0;
      var top = photo.getBoundingClientRect().top - els.scroll.getBoundingClientRect().top + els.scroll.scrollTop;
      return top + photo.offsetHeight / 2 - els.scroll.clientHeight;
    };
    var level = 0;
    if (short() > 0) {
      level = 1; applyFit(1);
      if (short() > 0) { level = 2; applyFit(2); }
    }
    readVp();
    fitBySize[vpKey] = level;
  }
  function fitOnOpen() {
    var known = isDesk() ? 0 : (vpKey ? fitBySize[vpKey] : undefined);
    if (known != null) { applyFit(known); return; }
    applyFit(0);
    afterPaint(fitPhone);
  }
  function onResize() {
    cancelAnimationFrame(fitFrame);
    fitFrame = requestAnimationFrame(fitPhone);
  }

  // The sheet's scroll goes back to the top when it closes (closeTimer), not here: writing scrollTop forces
  // a full layout, ~150 ms on a slow phone, inside the tap. Reopened before that ran: reset after paint.
  var closing = false;
  function show(opener) {
    clearTimeout(closeTimer);
    if (closing) { closing = false; afterPaint(function () { if (els.scroll) els.scroll.scrollTop = 0; }); }
    returnFocusTo = opener || (document.activeElement !== document.body ? document.activeElement : null);
    modal.removeAttribute('hidden');
    S.open = true;
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    document.documentElement.classList.add('pdp-xsell-modal-open');
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    // focus() forces a style recalc: after the first frame is painted, so it's not part of the tap (INP).
    afterPaint(function () {
      var c = S.open && modal.querySelector('.bb-close');
      if (c) c.focus({ preventScroll: true });
    });
  }

  function close(via) {
    if (!modal || !S.open) return;
    S.open = false;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('pdp-xsell-modal-open');
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', onResize);
    clearTimeout(closeTimer);
    closing = true;
    closeTimer = setTimeout(function () {
      closing = false;
      if (!modal || S.open) return;
      if (els.scroll) els.scroll.scrollTop = 0;
      modal.setAttribute('hidden', '');
    }, 300);
    if (via) track('sheet_bundle_sheet_close', { picked_count: pickedCount(), via: via });
    savePicks();
    refreshCard();
    var target = returnFocusTo && returnFocusTo.isConnected ? returnFocusTo :
      document.querySelector('#pdp-cross-sell-section .bb-card__cta');
    returnFocusTo = null;
    if (target) afterPaint(function () { if (target.isConnected && !S.open) target.focus({ preventScroll: true }); });
  }

  // The cart drawer closes on Escape *keyup*: when the sheet is open over it, that keyup must not
  // reach the drawer too.
  function swallowEscUp(e) {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    document.removeEventListener('keyup', swallowEscUp, true);
  }
  function onKey(e) {
    if (!S.open) return;
    if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation();
      document.addEventListener('keyup', swallowEscUp, true);
      close('esc');
      return;
    }
    if (e.key !== 'Tab') return;
    var f = [].filter.call(els.panel.querySelectorAll('button, [tabindex="0"]'), function (b) {
      return !b.disabled && b.offsetParent !== null;
    });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !els.panel.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !els.panel.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
  }

  function onModalClick(e) {
    var t = e.target.closest('[data-bb-close], [data-bb-remove], [data-bb-sheet], [data-bb-tile], [data-bb-size], [data-bb-cta]');
    if (!t) return;
    if (t.hasAttribute('data-bb-close')) { close(t.getAttribute('data-bb-close')); return; }
    if (S.adding) return;
    // detail 0 = the click came from the keyboard (Enter/Space), not a tap.
    if (t.hasAttribute('data-bb-remove')) removeSheet(parseInt(t.getAttribute('data-bb-remove'), 10), e.detail === 0);
    else if (t.hasAttribute('data-bb-sheet')) setActive(parseInt(t.getAttribute('data-bb-sheet'), 10), 'tap');
    else if (t.hasAttribute('data-bb-tile')) pickPrint(t.getAttribute('data-bb-tile'));
    else if (t.hasAttribute('data-bb-size')) { if (t.getAttribute('aria-disabled') !== 'true') setSize(t.getAttribute('data-bb-size')); }
    else if (t.hasAttribute('data-bb-cta')) cta();
  }

  // ---- Actions ---------------------------------------------------------------------------------
  // First sheet with no print, starting after `from` (wraps); -1 when all 3 have one.
  function nextOpen(slots, from) {
    for (var k = 1; k <= CFG.pick; k++) { var i = (from + k) % CFG.pick; if (!slots[i]) return i; }
    return -1;
  }
  // First sheet that can't be added yet: no print, no size, or its print isn't available in its size.
  function firstIncomplete() {
    for (var i = 0; i < CFG.pick; i++) {
      if (!S.slots[i] || !S.sz[i] || isSoldOut(S.slots[i], S.sz[i])) return i;
    }
    return -1;
  }
  function setActive(i, via) {
    if (i === S.active) return;
    S.active = i; S.hint = ''; S.note = '';
    track('sheet_bundle_sheet_select', { sheet: i + 1, via: via });
    renderAll();
  }
  // A tile becomes the editing sheet's print. The same print can go on more than one sheet: 2 or 3 of
  // the same colour (and size) is a valid Bundle — App A prices any `pick` tagged lines of the product.
  function pickPrint(name) {
    var a = S.active;
    if (allSold(name)) return;
    if (S.sz[a] && isSoldOut(name, S.sz[a])) {
      track('sheet_bundle_tile_unavailable_tap', { colour: name, size: S.sz[a] });
      return;
    }
    if (S.slots[a] === name) return;
    var slots = S.slots.slice();
    var mine = slots[a];
    slots[a] = name;
    var active = a;
    // Auto-advance only while the Bundle is being filled for the first time.
    if (!S.filled && S.sz[a]) { var nx = nextOpen(slots, a); if (nx >= 0) active = nx; }
    S.slots = slots;
    S.filled = S.filled || slots.every(Boolean);
    S.hint = ''; S.note = '';
    track('sheet_bundle_print_set', { sheet: a + 1, colour: name, replaced: mine || '', repeat: slots.filter(function (s) { return s === name; }).length > 1 });
    if (active !== a) { S.active = active; track('sheet_bundle_sheet_select', { sheet: active + 1, via: 'auto_advance' }); }
    savePicks();
    renderAll();
  }
  // × on a picked sheet: its colour comes off (its size stays) and it becomes the sheet being edited, so
  // the next print tapped goes on it. Filling resumes (auto-advance) until all 3 have a print again.
  function removeSheet(i, viaKeyboard) {
    var c = S.slots[i];
    if (!c) return;
    var slots = S.slots.slice();
    slots[i] = null;
    S.slots = slots;
    S.filled = false;
    S.active = i; S.hint = ''; S.note = '';
    track('sheet_bundle_print_remove', { sheet: i + 1, colour: c, mode: S.mode });
    savePicks();
    renderAll();
    // The × is gone with the re-render: a keyboard user's focus moves to that sheet (after paint: focus
    // forces a style recalc, ~100 ms on a slow phone). A tap needs no focus move.
    if (viaKeyboard) afterPaint(function () {
      var sheet = els.rail.querySelector('[data-bb-sheet="' + i + '"]');
      if (sheet) sheet.focus({ preventScroll: true });
    });
  }
  // The editing sheet's size. Sheets with no colour yet follow it (so 3 of one size is one change);
  // sheets already picked keep theirs.
  function setSize(z) {
    var a = S.active;
    if (S.slots[a] && isSoldOut(S.slots[a], z)) return;
    if (S.sz[a] === z) return;
    var from = S.sz[a];
    var fillsOthers = S.sz.some(function (x, i) { return i !== a && !S.slots[i] && x !== z; });
    S.sz = S.sz.map(function (x, i) { return i === a || !S.slots[i] ? z : x; });
    S.hint = ''; S.note = ''; S.pulseSize = false;
    track('sheet_bundle_size_set', { sheet: a + 1, from: from || '', to: z, filled_others: fillsOthers, size_source: S.ref ? S.ref.source : 'none' });
    savePicks();
    renderAll();
  }
  function unchanged() { return S.mode === 'edit' && S.orig === S.sz.join('|') + '|' + S.slots.join('|'); }
  // Greyed out (aria-disabled) until all 3 sheets can be added, but never truly disabled: tapped early,
  // it jumps to the first incomplete sheet and pulses what's missing.
  function cta() {
    if (S.adding) return;
    var k = firstIncomplete();
    if (k >= 0) {
      var needSize = !!S.slots[k] && (!S.sz[k] || isSoldOut(S.slots[k], S.sz[k]));
      track('sheet_bundle_cta_early', { picked_count: pickedCount(), sizes_known: S.sz.filter(Boolean).length });
      if (k !== S.active) track('sheet_bundle_sheet_select', { sheet: k + 1, via: 'cta_jump' });
      S.active = k; S.hint = ''; S.note = '';
      S.pulseRail = !needSize; S.pulseSize = needSize;
      renderAll();
      if (!isDesk() && els.scroll.scrollTo) els.scroll.scrollTo({ top: 0, behavior: 'smooth' });
      clearTimeout(pulseTimer);
      pulseTimer = setTimeout(function () {
        S.pulseRail = false; S.pulseSize = false;
        [].forEach.call(modal.querySelectorAll('.is-pulse'), function (p) { p.classList.remove('is-pulse'); });
      }, 1900);
      return;
    }
    if (unchanged()) { close('done'); return; }
    commit();
  }

  // ---- Cart writes -----------------------------------------------------------------------------
  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok || j.status) {
          var e = new Error(j.description || j.message || ('HTTP ' + r.status));
          e.status = r.status; e.url = url; e.response = j;
          throw e;
        }
        return j;
      });
    });
  }
  function toInt(v) { var n = parseInt(v, 10); return isFinite(n) && n > 0 ? n : 0; }
  // A failed cart write, with what was sent and what Shopify said, for whoever opens the console.
  function logFail(what, sent, err) {
    try {
      console.error('Bedsheet Bundle: ' + what + ' failed', { status: err && err.status, url: err && err.url,
        message: err && err.message, response: err && err.response, sent: sent });
    } catch (e) {}
  }
  function bundleItems() {
    var id = 'sb' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    return S.slots.map(function (c, i) {
      return { id: variantFor(c, S.sz[i]).id, quantity: S.mode === 'edit' ? S.editQty : 1,
        properties: { _sheet_bundle: CFG.title, _sheet_bundle_id: id, _sheet_bundle_slot: String(i + 1) } };
    });
  }
  // ---- Writes: one at a time, from the cart as rendered when they run --------------------------
  // The queue is shared with assets/cart-bedsheet-bundle.js (window.__bbCartQueue): a write starts only
  // after the previous one has finished, its re-render included, so it reads the server's latest state.
  function serial(task) {
    if (window.BedsheetBundleCart && window.BedsheetBundleCart.serial) return window.BedsheetBundleCart.serial(task);
    var run = (window.__bbCartQueue || Promise.resolve()).then(function () { return task(); });
    window.__bbCartQueue = run.then(function () {}, function () {});
    return run;
  }
  function parseJson(s, fallback) { try { return JSON.parse(s); } catch (e) { return fallback; } }
  // A nudge's sets, normalised so the copy taken at the tap and the one rendered now compare exactly.
  function setsSig(raw) { return JSON.stringify(parseJson(raw, []) || []); }
  // The cart as last rendered in this tab (every write re-renders it from its response): the /cart page's
  // items on /cart, else the drawer. Its bundles, and the nudge's sets. No cart markup here: /cart.js.
  function cartView() {
    var root = document.getElementById('main-cart-items') || document.querySelector('cart-drawer');
    if (root) {
      var nudge = root.querySelector('[data-bb-nudge]');
      return Promise.resolve({
        bundles: [].map.call(root.querySelectorAll('[data-bb-line]'), function (l) {
          return { id: l.getAttribute('data-bundle-id'), qty: parseInt(l.getAttribute('data-qty'), 10) || 0,
            keys: parseJson(l.getAttribute('data-keys'), []), items: parseJson(l.getAttribute('data-items'), []),
            title: l.getAttribute('data-title') || CFG.title };
        }),
        sets: nudge ? setsSig(nudge.getAttribute('data-keys')) : null
      });
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (cart) {
        var by = {}, order = [];
        (cart.items || []).forEach(function (it) {
          var id = it.properties && it.properties._sheet_bundle_id;
          if (!id) return;
          if (!by[id]) { by[id] = { id: id, qty: it.quantity, keys: [], items: [], title: it.properties._sheet_bundle || CFG.title }; order.push(id); }
          var b = by[id];
          b.qty = Math.min(b.qty, it.quantity);
          b.keys.push(it.key);
          b.items.push({ id: it.variant_id, qty: it.quantity, slot: it.properties._sheet_bundle_slot || '' });
        });
        return { bundles: order.map(function (id) { return by[id]; }), sets: null };
      });
  }
  function commit() {
    if (S.adding) return;
    // Everything this write needs, taken now: the sheet can be closed or reopened while it runs.
    var job = {
      state: S, mode: S.mode, sizes: S.sz.slice(), items: bundleItems(), q: quote(S.slots, S.sz),
      baseKeys: S.baseKeys.slice(), baseSig: S.baseSig, editId: S.editId
    };
    var mode = job.mode, q = job.q, sizes = job.sizes;
    var mine = function () { return S === job.state; }; // still the sheet on screen
    S.adding = true; S.hint = ''; S.note = '';
    renderFoot();
    track('sheet_bundle_add_attempt', {
      colours: S.slots.join('|'), sizes: sizes.join('|'), mixed: sizes.some(function (z) { return z !== sizes[0]; }),
      price: Math.round(q.bundle / 100), mode: mode
    });
    var LIMIT = 'limit', CHANGED = 'changed';
    var finished = false;
    // Done as soon as Shopify confirms: the sheet closes; once the cart is re-rendered, the drawer
    // opens on it (no toast). On /cart the page itself shows the change.
    function done() {
      if (finished) return;
      finished = true;
      job.state.adding = false;
      if (mine()) { clearPicks(); S.slots = [null, null, null]; close(); }
      track('sheet_bundle_add_result', { result: mode === 'complete' ? 'completed' : (mode === 'edit' ? 'updated' : 'added'), reason: '', sizes: composition(sizes) });
    }
    serial(function () {
      var removed = [];
      return cartView()
        .then(function (view) {
          // "Make it a Bundle": the loose sets tapped must still be the cart's, else don't guess.
          if (mode === 'complete' && view.sets !== job.baseSig) throw new Error(CHANGED);
          if (mode === 'edit') {
            // Replace the Bundle's lines (remove, then add the new ones at its current quantity; put
            // back if that add fails). Gone from the cart meanwhile? Then it's a plain add.
            var old = view.bundles.filter(function (b) { return b.id === job.editId; })[0];
            if (old) {
              var drop = {};
              old.keys.forEach(function (k) { drop[k] = 0; });
              old.items.forEach(function (it) {
                removed.push({ id: it.id, quantity: it.qty || 1,
                  properties: { _sheet_bundle: old.title, _sheet_bundle_id: old.id, _sheet_bundle_slot: String(it.slot || '') } });
              });
              job.items.forEach(function (it) { it.quantity = old.qty || 1; });
              return postJson('/cart/update.js', { updates: drop });
            }
            job.items.forEach(function (it) { it.quantity = 1; });
          }
          var count = view.bundles.reduce(function (n, b) { return n + b.qty; }, 0);
          if (count >= maxBundles()) throw new Error(LIMIT);
          if (mode === 'complete' && job.baseKeys.length) {
            // The loose sets become part of the bundle: one unit off their line per sheet used (a line
            // supplies two or three when it's the same colour twice), then the 3 bundle lines.
            var need = {};
            job.baseKeys.forEach(function (k) {
              need[k.key] = (need[k.key] || 0) + 1;
              removed.push({ id: k.id, quantity: 1 });
            });
            // New quantity per line = its quantity − the units used, a whole number ≥ 0.
            var build = function (qtyOf) {
              var updates = {};
              Object.keys(need).forEach(function (key) { updates[key] = Math.max(0, toInt(qtyOf(key)) - need[key]); });
              return updates;
            };
            var rendered = {};
            job.baseKeys.forEach(function (k) { rendered[k.key] = k.qty; });
            var first = build(function (key) { return rendered[key]; });
            return postJson('/cart/update.js', { updates: first }).catch(function (err) {
              // Rejected: re-derive from the cart as the server has it, once. Lines gone, or fewer units
              // than the sheets use? The cart changed under us: don't guess.
              logFail('convert update', { updates: first }, err);
              return fetch('/cart.js', { headers: { Accept: 'application/json' } })
                .then(function (r) { return r.json(); })
                .then(function (cart) {
                  var have = {};
                  (cart.items || []).forEach(function (it) { have[it.key] = it.quantity; });
                  if (Object.keys(need).some(function (key) { return !(toInt(have[key]) >= need[key]); })) throw new Error(CHANGED);
                  var retry = build(function (key) { return have[key]; });
                  return postJson('/cart/update.js', { updates: retry }).catch(function (err2) {
                    logFail('convert update (retry)', { updates: retry }, err2);
                    throw err2;
                  });
                });
            });
          }
        })
        .then(function () {
          return postJson('/cart/add.js', { items: job.items, sections: cartSections(), sections_url: window.location.pathname })
            .catch(function (err) {
              if (!removed.length) throw err;
              // Put the removed lines back and show the cart as it really is, then report the error.
              return postJson('/cart/add.js', { items: removed, sections: cartSections(), sections_url: window.location.pathname })
                .then(function (res) { return refreshDrawer(res); }, function () { return refreshDrawer(null); })
                .then(function () { throw err; });
            });
        })
        .then(function (res) {
          // Close now, then re-render and show the cart. The re-render stays inside this task, so the
          // next queued write still reads the new cart.
          done();
          return Promise.resolve(refreshDrawer(res)).then(showCart);
        });
    })
      .catch(function (err) {
        if (finished) { console.error('Bedsheet Bundle re-render:', err); return; } // the write itself succeeded
        job.state.adding = false;
        var reason = err && err.message;
        var msg;
        if (reason === CHANGED) {
          track('sheet_bundle_add_result', { result: 'error', reason: 'cart_changed' });
          if (mine()) close();
          toast('Your cart just changed. Tap the offer again.');
          return;
        }
        if (reason === LIMIT) {
          msg = maxBundles() > 1 ? 'You can add up to ' + maxBundles() + ' ' + CFG.title + 's per order.' : 'You already have a ' + CFG.title + ' in your cart.';
          track('sheet_bundle_limit', { where: 'sheet', max: maxBundles() });
          track('sheet_bundle_add_result', { result: 'error', reason: 'limit' });
        } else {
          // Shopify's own wording ("updates parameter is invalid") is for us, not the shopper: logged.
          logFail(mode + ' bundle', { items: job.items }, err);
          msg = "Couldn't " + (mode === 'edit' ? 'update' : 'add') + ' your Bundle. Please try again.';
          track('sheet_bundle_add_result', { result: 'error', reason: String(reason || '').slice(0, 120) });
        }
        if (mine() && S.open) { S.hint = msg; renderFoot(); } else toast(msg);
      });
  }
  // Section ids for a cart write: the drawer's, plus the /cart page's on /cart (cart-bedsheet-bundle.js).
  function cartSections() {
    return window.BedsheetBundleCart ? window.BedsheetBundleCart.sections() : SECTIONS;
  }
  // Re-render the drawer (and /cart) from the write's response, then publish cartUpdate: the rail
  // (pdp-cross-sell.js) re-reads the cart on it and hands it to syncCart() → the card's in-cart state.
  function refreshDrawer(res) {
    if (window.BedsheetBundleCart) return window.BedsheetBundleCart.render(res);
    var cd = document.querySelector('cart-drawer');
    if (cd) {
      if (res && res.sections && typeof cd.renderContents === 'function') cd.renderContents(res);
      else if (typeof cd.refresh === 'function') cd.refresh();
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bedsheet-bundle', cartData: res });
    }
  }
  // After a bundle write: open the cart drawer on the new cart. Not on /cart (the page shows it), and not
  // again when it's already open (a "Make it a Bundle" from the drawer).
  function showCart() {
    if (document.getElementById('main-cart-items')) return;
    var cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.open === 'function' && !cd.classList.contains('active')) cd.open();
  }
  function openDrawer() {
    var cd = document.querySelector('cart-drawer');
    if (window.BedsheetBundleCart) window.BedsheetBundleCart.hideToast();
    if (cd && typeof cd.open === 'function') cd.open();
    else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }
  // The toast lives in cart-bedsheet-bundle.js (loaded on every page with the cart drawer).
  function toast(text, action, sub) {
    if (window.BedsheetBundleCart) window.BedsheetBundleCart.toast(text, action, sub);
  }

  // ==== Render ===================================================================================
  function renderAll() {
    els.pill.textContent = CFG.pct + '% OFF';
    renderRail();
    renderSize();
    if (!els.tiles.firstChild) renderTiles(); else patchTiles();
    renderFoot();
  }

  // The 3 sheets: a 3-across rail on phones, a stacked list on desktop (same markup, CSS layout).
  function renderRail() {
    var n = pickedCount();
    var sheets = S.slots.map(function (c, i) {
      var z = S.sz[i], active = i === S.active, info = z ? sizeInfo(z) : null;
      // Each sheet + its × (picked sheets only) in a wrapper: a button can't hold another button.
      return '<div class="bb-sheet-wrap' + (c ? ' is-filled' : '') + '">' +
        '<button type="button" class="bb-sheet' + (active ? ' is-active' : '') + (c ? ' is-filled' : ' is-empty') +
          (active && S.pulseRail ? ' is-pulse' : '') + '" data-bb-sheet="' + i + '" aria-pressed="' + active + '"' +
          ' aria-label="' + esc('Sheet ' + (i + 1) + ': ' + (c || 'no colour yet') + ', ' + (info ? info.say : 'size not chosen') + (active ? '. Editing' : '. Tap to edit')) + '">' +
        '<span class="bb-sheet__num" aria-hidden="true">' + (i + 1) + '</span>' +
        '<span class="bb-sheet__thumb">' + (c ? '<img src="' + esc(colour(c).img) + '" alt="" decoding="async">' : '<span class="bb-sheet__plus" aria-hidden="true">+</span>') + '</span>' +
        '<span class="bb-sheet__name">' + (c ? esc(c) : 'Pick a colour') + '</span>' +
        '<span class="bb-sheet__chip' + (info ? '' : ' is-ask') + '">' + (info ? esc(info.label) : 'Size?') + '</span>' +
      '</button>' +
      (c ? '<button type="button" class="bb-sheet__remove" data-bb-remove="' + i + '" aria-label="' + esc('Remove ' + c + ' from sheet ' + (i + 1)) + '">' + ICON_X + '</button>' : '') +
      '</div>';
    }).join('');
    els.rail.innerHTML =
      '<div class="bb-sheets" role="group" aria-label="Your 3 bedsheets">' + sheets + '</div>' +
      '<div class="bb-progress"><span class="bb-progress__text" aria-live="polite">' + n + ' of ' + CFG.pick + ' chosen</span>' +
        '<span class="bb-progress__bar" aria-hidden="true"><span class="bb-progress__fill' + (n === CFG.pick ? ' is-done' : '') + '" style="width:' + Math.round(n / CFG.pick * 100) + '%"></span></span></div>';
  }

  // Size for the sheet being edited: "Sheet N · Size" and a 2-option control.
  function renderSize() {
    var a = S.active, za = S.sz[a], c = S.slots[a], ref = S.ref;
    var help = '', tone = 'muted';
    if (!za) { help = 'Choose a size'; tone = 'ask'; }
    else if (ref && za === ref.size) { help = ref.text; tone = 'ok'; }
    var opts = CFG.sizes.map(function (z) {
      var on = za === z.value, off = !!c && isSoldOut(c, z.value);
      return '<button type="button" role="radio" class="bb-seg__opt' + (on ? ' is-on' : '') + (off ? ' is-off' : '') + '" data-bb-size="' + esc(z.value) + '"' +
          ' aria-checked="' + on + '" aria-disabled="' + off + '" aria-label="' + esc(z.say + (off ? ', not available in this print' : '')) + '">' +
        '<span class="bb-seg__label">' + esc(z.label) + '</span><span class="bb-seg__dims">' + (off ? 'Not in this print' : esc(z.value)) + '</span></button>';
    }).join('');
    els.size.innerHTML =
      '<div class="bb-size__head"><span class="bb-size__title" id="bbSizeLabel">Sheet ' + (a + 1) + ' · Size</span>' +
        (help ? '<span class="bb-size__help is-' + tone + '">' + esc(help) + '</span>' : '') + '</div>' +
      '<div class="bb-seg' + (S.pulseSize ? ' is-pulse' : '') + '" role="radiogroup" aria-labelledby="bbSizeLabel">' + opts + '</div>';
  }

  // The 8 prints: built once (photos stay put), then only their state changes (patchTiles).
  function renderTiles() {
    var html = '';
    (CFG.groups || []).forEach(function (g) {
      var list = colours.filter(function (c) { return c.cat === g.cat; });
      if (!list.length) return;
      html += '<div class="bb-group" role="group" aria-label="' + esc(g.title) + '">' +
        '<div class="bb-group__head"><span class="bb-group__title">' + esc(g.title) + '</span><span class="bb-group__sub">' + list.length + ' colours</span></div>' +
        '<div class="bb-group__grid">' +
        list.map(function (c) {
          return '<button type="button" class="bb-tile" data-bb-tile="' + esc(c.name) + '">' +
            // Not lazy: images inside the hidden sheet would only start when it opens. data-src until
            // loadPhotos() (the rail near the screen, or the sheet opening).
            '<span class="bb-tile__photo"><img ' + (photosOn ? 'src' : 'data-src') + '="' + esc(c.img) + '" alt="" decoding="async">' +
              '<span class="bb-tile__flag" aria-hidden="true"></span></span>' +
            '<span class="bb-tile__name">' + esc(c.name) + '</span></button>';
        }).join('') +
        '</div></div>';
    });
    els.tiles.innerHTML = html;
    if (S.open) patchTiles();
  }
  // The 8 tiles show the EDITING sheet only: its colour is selected when it has one (none when it's
  // empty), plus what can't be picked in its size (sold out / not in this size). The other sheets'
  // picks are shown in the tray above, not on the tiles.
  function patchTiles() {
    var a = S.active, za = S.sz[a], cur = S.slots[a];
    [].forEach.call(els.tiles.querySelectorAll('[data-bb-tile]'), function (b) {
      var name = b.getAttribute('data-bb-tile');
      var on = name === cur;
      var both = allSold(name);
      var na = !both && !!za && isSoldOut(name, za);
      var one = !both && CFG.sizes.some(function (z) { return isSoldOut(name, z.value); });
      var left = one ? CFG.sizes.filter(function (z) { return !isSoldOut(name, z.value); })[0] : null;
      var partial = !both && !na && !za && one;
      var flag = both ? 'SOLD OUT' : (na ? 'Not in ' + sizeInfo(za).label : (partial ? left.label + ' only' : ''));
      b.className = 'bb-tile' + (on ? ' is-current' : '') + (both ? ' is-soldout' : '') + (na ? ' is-na' : '') + (partial ? ' is-partial' : '');
      b.setAttribute('aria-pressed', String(on));
      b.setAttribute('aria-disabled', String(both || na));
      b.setAttribute('aria-label', name + (on ? ', on sheet ' + (a + 1) : '') +
        (both ? ', sold out' : (na ? ', not in ' + sizeInfo(za).label : (on ? '' : '. Set as sheet ' + (a + 1)))));
      b.querySelector('.bb-tile__flag').textContent = flag;
    });
  }

  // Footer: price (+ "You save" at 3 of 3), one helper line, the button.
  function renderFoot() {
    var n = pickedCount();
    var k = firstIncomplete();
    var complete = k < 0;
    var unknown = S.sz.some(function (z) { return !z; });
    var q = quote(S.slots, S.sz);
    var a = S.active, missing = CFG.pick - n;

    var label;
    if (S.adding) label = 'Adding…';
    else if (S.mode === 'edit') label = unchanged() ? 'Done' : 'Update bundle · ' + rupees(q.bundle);
    else if (S.mode === 'complete') label = 'Make it a bundle · ' + rupees(q.bundle);
    else label = 'Add bundle · ' + rupees(q.bundle);

    // One line above the button: an error/limit hint, else a note, else what's missing.
    var line = '', tone = 'hint';
    if (S.hint) line = S.hint;
    else if (S.note) { line = S.note; tone = 'note'; }
    else if (!S.sz[a]) line = 'Choose a size for sheet ' + (a + 1);
    else if (missing > 0) line = 'Pick ' + missing + ' more bedsheet' + (missing > 1 ? 's' : '') + ' to unlock ' + CFG.pct + '% off';
    else if (!complete) line = 'Choose a size for sheet ' + (k + 1);

    els.foot.innerHTML =
      '<div class="bb-price">' +
        '<div class="bb-price__line">' + (unknown ? '<span class="bb-price__from">From</span>' : '') +
          '<span class="bb-price__now" aria-live="polite">' + rupees(q.bundle) + '</span><s class="bb-price__was">' + rupees(q.full) + '</s></div>' +
        (complete ? '<span class="bb-price__save" aria-live="polite">You save ' + rupees(q.save) + '</span>' : '') +
      '</div>' +
      (line ? '<div class="bb-helper is-' + tone + '" role="status">' + (tone === 'note' ? ICON_INFO : '') + '<span>' + esc(line) + '</span></div>' : '') +
      '<button type="button" class="bb-cta' + (complete || S.adding ? ' is-ready' : '') + '" data-bb-cta' +
        (S.adding ? ' aria-busy="true"' : (complete ? '' : ' aria-disabled="true"')) + '>' +
        (S.adding ? '<span class="bb-cta__spin" aria-hidden="true"></span>' : '') + esc(label) + '</button>';
  }

  // ---- Rail card taps --------------------------------------------------------------------------
  // Capture phase + stopImmediatePropagation, like the rail's own options button
  // (pdp-cross-sell.js): the page's bubbling click listeners (tags, trackers) don't run for this tap,
  // which keeps the tap responsive. The bundle sends its own analytics events.
  document.addEventListener('click', function (e) {
    var b = e.target.closest('#pdp-cross-sell-section .bb-card__cta');
    if (!b) return;
    e.stopImmediatePropagation();
    var card = b.closest('.bb-card');
    var state = card ? card.getAttribute('data-state') : '';
    track('sheet_bundle_card_tap', { state: state });
    // Cap reached: the card opens the cart (edits happen on each bundle line there). Otherwise it always
    // starts a NEW bundle, whether or not the cart already has some.
    if (state === 'full') { openDrawer(); return; }
    open({ mode: 'new', source: 'rail', opener: b });
  }, true);

  window.BedsheetBundle = {
    /** Rail card HTML for this PDP's variant ('' when the bundle doesn't apply). */
    cardHtml: function (variantId) { try { return cardHtml(variantId); } catch (e) { console.error('Bedsheet Bundle card:', e); return ''; } },
    /** Rail position (1-based). */
    slot: function () { return (CFG && CFG.slot) || 1; },
    /** Cart snapshot from the rail: the card's in-cart state and the cart's bed size. */
    syncCart: function (cart) { try { syncCart(cart); } catch (e) {} },
    /** Open the sheet. { mode: 'new' | 'complete' | 'edit', base: [colour, …] (up to 3 loose sets), sizes: [size, …], keys: [{key, qty, id}, …] (one per set), config, source, opener } */
    open: open,
    close: close,
    toast: toast,
    /** Whether the sheet is open. */
    isOpen: function () { return !!(S && S.open); },
    /** Hand over the sheet product's /products/<handle>.js (the cart nudge fetches it alongside this
     *  script), so opening needs no request. No-op once the variants are known. */
    prime: function (cfg, productJs) {
      try {
        if ((CFG && CFG.variants) || !cfg || !productJs || !productJs.variants) return;
        setConfig(Object.assign({}, cfg, { variants: variantsFromProductJs(productJs) }));
      } catch (e) {}
    }
  };
})();
